import { parseChord } from './chords/chord'
import type { RevisionContent } from './schema/v1'
import { isNormalized } from './text'

/**
 * Reglas que el esquema no puede expresar: referencias entre IDs, anclajes dentro del texto,
 * acordes reconocibles y un mapa de tiempos coherente con la secuencia. Un formato correcto no
 * garantiza acordes correctos (§9.3), así que esto se comprueba aparte.
 */
export type IssueSeverity = 'error' | 'warning'

export interface Issue {
  severity: IssueSeverity
  code: string
  path: string
  message: string
}

export function validateContent(content: RevisionContent): Issue[] {
  const issues: Issue[] = []
  const error = (code: string, path: string, message: string) =>
    issues.push({ severity: 'error', code, path, message })
  const warning = (code: string, path: string, message: string) =>
    issues.push({ severity: 'warning', code, path, message })

  const sectionIds = new Set<string>()
  const lineIds = new Set<string>()
  const chordIds = new Set<string>()
  const chordsBySection = new Map<string, string[]>()

  content.sections.forEach((section, sectionIndex) => {
    const sectionPath = `sections[${sectionIndex}]`
    if (sectionIds.has(section.id))
      error('id.duplicate', sectionPath, `Sección repetida: ${section.id}`)
    sectionIds.add(section.id)
    const sectionChords: string[] = []
    chordsBySection.set(section.id, sectionChords)

    section.lines.forEach((line, lineIndex) => {
      const linePath = `${sectionPath}.lines[${lineIndex}]`
      if (lineIds.has(line.id)) error('id.duplicate', linePath, `Línea repetida: ${line.id}`)
      lineIds.add(line.id)
      if (!isNormalized(line.text)) error('text.not-nfc', linePath, 'El texto no está en NFC')
      if (line.kind === 'comment' && line.chords.length > 0) {
        error('line.comment-with-chords', linePath, 'Un comentario no lleva acordes')
      }

      let previousAt = 0
      line.chords.forEach((anchor, chordIndex) => {
        const chordPath = `${linePath}.chords[${chordIndex}]`
        if (chordIds.has(anchor.id))
          error('id.duplicate', chordPath, `Acorde repetido: ${anchor.id}`)
        chordIds.add(anchor.id)
        sectionChords.push(anchor.id)
        if (anchor.at > line.text.length) {
          error('anchor.out-of-range', chordPath, `El acorde ${anchor.chord} cae fuera del texto`)
        }
        if (anchor.at < previousAt) {
          error('anchor.unordered', chordPath, 'Los acordes de la línea no están en orden')
        }
        previousAt = anchor.at
        if (!parseChord(anchor.chord)) {
          warning('chord.unrecognized', chordPath, `Acorde por revisar: ${anchor.chord}`)
        }
      })
    })
  })

  const occurrenceSection = new Map<string, string>()
  content.sequence.forEach((occurrence, index) => {
    const path = `sequence[${index}]`
    if (occurrenceSection.has(occurrence.id)) {
      error('id.duplicate', path, `Aparición repetida: ${occurrence.id}`)
    }
    if (!sectionIds.has(occurrence.section)) {
      error('sequence.unknown-section', path, `La secuencia apunta a una sección que no existe`)
    }
    occurrenceSection.set(occurrence.id, occurrence.section)
  })

  validateBeatMap(content, occurrenceSection, chordsBySection, error)
  return issues
}

function validateBeatMap(
  content: RevisionContent,
  occurrenceSection: Map<string, string>,
  chordsBySection: Map<string, string[]>,
  error: (code: string, path: string, message: string) => void,
): void {
  const { beatMap } = content
  if (beatMap.status === 'absent') {
    if (beatMap.events.length > 0) {
      error('beatmap.absent-with-events', 'beatMap', 'Un mapa ausente no puede tener eventos')
    }
    return
  }

  // Los eventos recorren la secuencia en orden y, dentro de cada aparición, sus acordes en orden.
  const sequenceIndex = new Map(content.sequence.map((occurrence, index) => [occurrence.id, index]))
  let lastSequence = -1
  let lastChordIndex = -1
  beatMap.events.forEach((event, index) => {
    const path = `beatMap.events[${index}]`
    const section = occurrenceSection.get(event.occurrence)
    const position = sequenceIndex.get(event.occurrence)
    if (section === undefined || position === undefined) {
      error('beatmap.unknown-occurrence', path, 'El evento apunta a una aparición que no existe')
      return
    }
    if (position < lastSequence) {
      error('beatmap.unordered', path, 'Los eventos no siguen el orden de la secuencia')
      return
    }
    if (position > lastSequence) {
      lastSequence = position
      lastChordIndex = -1
    }
    if (event.kind === 'chord') {
      const chordIndex = chordsBySection.get(section)?.indexOf(event.chord) ?? -1
      if (chordIndex < 0) {
        error('beatmap.unknown-chord', path, 'El evento apunta a un acorde de otra sección')
        return
      }
      if (chordIndex < lastChordIndex) {
        error('beatmap.unordered', path, 'Los acordes de la aparición no están en orden')
      }
      lastChordIndex = chordIndex
    }
  })
}

export function hasErrors(issues: readonly Issue[]): boolean {
  return issues.some((issue) => issue.severity === 'error')
}
